/**
 * Hook to check whether the Rovo Insights feature is available for the current user.
 *
 * Uses RovoInsightsService (extends AssistanceServiceBase) so that Rovo Insights
 * concerns stay out of the general-purpose AssistanceService.
 */
import { useMemo } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { RovoInsightsService } from '@atlassian/rovo-insights-service/service';

export interface UseInsightsStatusResult {
	isInsightsAvailable: boolean;
	isLoading: boolean;
}

interface UseInsightsStatusProps extends AssistanceServiceInjectedParams {
	enabled: boolean;
}

export const useInsightsStatus: (
	props: Omit<UseInsightsStatusProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseInsightsStatusResult = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		enabled,
	}: UseInsightsStatusProps) => {
		const insightsService = useMemo(() => {
			const config = assistanceService.getConfig();
			return new RovoInsightsService(config);
		}, [assistanceService]);

		const { data, isFetching } = useQuery<{ insights_available: boolean }>({
			queryKey: ['insightsStatus'],
			enabled,
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_INSIGHTS_STATUS,
					name: ExperienceName.FETCH_INSIGHTS_STATUS,
				});
				try {
					const response = await insightsService.getInsightsStatus();
					experienceTracker.succeed({
						name: ExperienceName.FETCH_INSIGHTS_STATUS,
					});
					return response;
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_INSIGHTS_STATUS,
						error: error instanceof Error ? error : new Error(String(error)),
					});
					throw error;
				}
			},
		});

		return {
			isInsightsAvailable: Boolean(data?.insights_available),
			isLoading: isFetching && data === null,
		};
	},
);
